/**
 * Which credential actually serves a search provider, as a pure function.
 *
 * WHY THIS IS NOT INLINE IN THE TABLE: the badge has to keep TWO independent
 * facts independent, and the whole failure mode is collapsing them. `orgEntry`
 * (this org has its own key) and `platformFallback` (the platform plane can
 * serve this provider) are separate booleans on the S1 payload precisely
 * because an org key that fails to decrypt falls through to the platform, and
 * an admin needs to see BOTH. The server's own contract says as much
 * (`search-provider-status.ts`), and keeps `effective` distinct from
 * `hasUsableCredential` "so a disabled org and a keyless org are never rendered
 * the same way".
 *
 * ⇒ A DISABLED org holding a valid key must read as disabled-but-keyed, never
 * as unconfigured. Rendering those alike is the same failed-read-as-value shape
 * this codebase has hit repeatedly. [[unknown-must-be-representable]]
 *
 * Agreed with `search-providers` at handshake `20260813T222927Z`; plan of
 * record `ai-docs/search-providers-portal-ui-miniplan.md`.
 */
/**
 * `disabled` is carried ALONGSIDE the source rather than replacing it — a
 * disabled org still has (or lacks) a key, and the badge must say which.
 *
 * ⚖ THE PLATFORM HALF DEFERS TO THE SERVER'S VERDICT (ruling 2026-08-15, found
 * in live data). `platformFallback` answers "is a key STORED"; the badge asks
 * "which key SERVES me". Those come apart for real: ⚖ A-10 keeps brave's
 * platform key STORED for rotation continuity while `isPlatformServingDisabled`
 * stops it serving, so `arc-advogados` really does return
 * `platformFallback: true` beside `effectiveSource: "none"` — and reading only
 * the boolean rendered "platform default" for a provider nothing serves.
 *
 * This does NOT weaken the two-independent-facts rule; it puts each fact on its
 * proper surface. `orgEntry` stays authoritative for the ORG half precisely
 * because a stored-but-undecryptable org key must still read as present.
 */
export function searchProviderBadge(i) {
    const disabled = !i.enabled;
    if (i.orgEntry)
        return { kind: "org-key", disabled };
    // "Nothing serves this" beats "a key is stored somewhere".
    if (i.effectiveSource === "none")
        return { kind: "not-configured", disabled };
    if (i.platformFallback)
        return { kind: "platform-default", disabled };
    return { kind: "not-configured", disabled };
}
/**
 * Which providers a credential row may offer.
 *
 * ⭐ KEYLESS IS READ OFF THE ROW, NEVER RE-DERIVED. The server owns the keyless
 * set (`resolve-search-credential.ts:48`) and reports the verdict per row as
 * `effectiveSource: "keyless"`. Hand-mirroring that set here would be a rule we
 * cannot test against its source — it would pass while silently disagreeing the
 * day a second keyless provider is added. Reading the row's own
 * self-description means this function needs no update when that happens.
 *
 * ⚠ WHY KEYLESS IS EXCLUDED AT ALL: every row of this table is
 * `kind: "api-key"` with a mandatory secret input. Offering a keyless provider
 * asks an admin for a key that does not exist — the server's own words are
 * "nothing is missing; this is not a configuration problem". Rendering
 * "needs no key" and "needs a key you haven't set" alike is the collapse this
 * whole module exists to prevent. [[unknown-must-be-representable]]
 *
 * ⚠ `current` IS UNCONDITIONAL — including when it is keyless. A `<select>`
 * whose `value` is absent from its options renders BLANK, so filtering an
 * existing row's own provider would make it silently vanish from its own row.
 * We filter what is OFFERED, never what is already held.
 */
export function searchProviderOptions(i) {
    const offered = (i.status ?? [])
        .filter((r) => r.effectiveSource !== "keyless")
        .map((r) => r.provider);
    // Seeds first: `seeds[0]` is the deliberate default for a newly-added row.
    return [...new Set([...i.seeds, ...offered, ...(i.current ? [i.current] : [])])];
}
//# sourceMappingURL=search-provider-source.js.map